import React, { createContext, useCallback, useState, useContext } from 'react'
import jwtDecode, { JwtPayload } from 'jwt-decode'

import api from '../services/api'
import { LOCAL_STORAGE_KEYS } from 'common/constants'
interface Menu {
  id: number
  parent_id?: number
  method?: string
  name: string
  url?: string
  permission: boolean
  children?: Menu[]
  type: string
  icon: string
}

interface AuthState {
  token: string
  user: User
  menus: Menu[]
}

interface SingInCredentials {
  username: string
  password: string
}

interface AuthContextData {
  user: User
  menus: Menu[]
  backupHistory: string
  signIn(credentials: SingInCredentials): Promise<void>
  signOut(): void
  userLogged(): boolean
  updateUser(): void
  setHistory(string: string): void
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export const AuthProvider: React.FC = ({ children }) => {
  const [backupHistory, setBackupHistory] = useState('')
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN)
    const user = localStorage.getItem(LOCAL_STORAGE_KEYS.USER)
    const menus = localStorage.getItem(LOCAL_STORAGE_KEYS.MENUS)

    if (token && user && menus) {
      api.defaults.headers.authorization = `Bearer ${token}`
      return { token, user: JSON.parse(user), menus: JSON.parse(menus) }
    }
    return {} as AuthState
  })

  const signIn = useCallback(async ({ username, password }) => {
    const response = await api.post('sessions', {
      username,
      password
    })

    const { token, user, menus } = response.data

    localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, token)
    localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user))
    localStorage.setItem(LOCAL_STORAGE_KEYS.MENUS, JSON.stringify(menus))

    api.defaults.headers.authorization = `Bearer ${token}`

    setData({ token, user, menus })
  }, [])

  const setHistory = useCallback((history: string) => {
    setBackupHistory(history)
  }, [])
  const userLogged = useCallback(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN)
    if (token) {
      const decode = jwtDecode<JwtPayload>(token)
      const currentDate = Date.now().valueOf() / 1000
      if (decode.exp < currentDate) {
        return false
      } else {
        return true
      }
    }
    return false
  }, [])

  const updateUser = useCallback(async () => {
    const user = await (await api.get('/profile/show')).data

    setData({ token: data.token, user, menus: data.menus })

    localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user))
  }, [data.menus, data.token])

  const signOut = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN)
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER)
    localStorage.removeItem(LOCAL_STORAGE_KEYS.MENUS)
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SELECTED_MENU)
    setBackupHistory('')
    setData({} as AuthState)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        setHistory,
        backupHistory,
        user: data.user,
        menus: data.menus,
        signIn,
        signOut,
        userLogged,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AutProvider')
  }

  return context
}
